<template>
  <div class="form-container">
    <v-card class="form-card" elevation="2">
      <!-- Cabeçalho -->
      <v-card-title class="form-header">
        <span class="form-title">{{ isEditMode ? 'Editar Seção' : 'Cadastro de Seção' }}</span>
        <v-checkbox
          v-model="formData.ativo"
          label="Ativo"
          color="primary"
          hide-details
          class="ativo-checkbox"
        />
      </v-card-title>

      <!-- Formulário -->
      <v-card-text class="form-content">
        <v-form ref="form" v-model="isFormValid">
          <!-- Linha 1: Código Seção -->
          <v-row class="form-row">
            <v-col cols="12" sm="6" md="4" class="pa-1">
              <div class="form-label">Código Seção</div>
              <v-text-field
                v-model="formData.codigoSecao"
                type="text"
                outlined
                dense
                :rules="[rules.required, rules.numeric, rules.numericOnly]"
                placeholder="Digite o código"
                class="form-field"
                hide-details="auto"
                @input="onNumericInput('codigoSecao', $event)"
                @keypress="onlyNumbers"
                @paste="onPaste"
              />
            </v-col>
          </v-row>

          <!-- Linha 2: Descrição -->
          <v-row class="form-row">
            <v-col cols="12" sm="12" md="8" class="pa-1">
              <div class="form-label">Descrição</div>
              <v-text-field
                v-model="formData.descricao"
                outlined
                dense
                :rules="[rules.required, rules.alphaNumeric]"
                placeholder="Digite a descrição"
                class="form-field"
                hide-details="auto"
              />
            </v-col>
          </v-row>
        </v-form>
      </v-card-text>

      <!-- Rodapé com Botões -->
      <v-card-actions class="form-actions">
        <v-btn
          @click="handleCancel"
          class="action-btn cancel-btn grey-btn"
        >
          Cancelar
        </v-btn>
        <v-btn
          @click="handleSave"
          color="primary"
          class="action-btn save-btn"
          :loading="isSaving"
        >
          <v-icon left>mdi-content-save</v-icon>
          Salvar
        </v-btn>
      </v-card-actions>
    </v-card>

    <!-- Snackbar para mensagens -->
    <v-snackbar
      v-model="showSnackbar"
      :color="snackbarColor"
      :timeout="3000"
      top
    >
      {{ snackbarMessage }}
      <template v-slot:action="{ attrs }">
        <v-btn
          text
          v-bind="attrs"
          @click="showSnackbar = false"
        >
          Fechar
        </v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
export default {
  name: 'SecaoForm',
  props: {
    initialData: {
      type: Object,
      default: () => ({})
    },
    isEditMode: {
      type: Boolean,
      default: false
    },
    secaoId: {
      type: [String, Number],
      default: null
    }
  },
  data() {
    return {
      isFormValid: false,
      isSaving: false,
      showSnackbar: false,
      snackbarMessage: '',
      snackbarColor: 'success',
      formData: {
        codigoSecao: '',
        descricao: '',
        ativo: true
      },
      rules: {
        required: v => !!v || 'Campo obrigatório',
        numeric: v => !v || /^\d+$/.test(v) || 'Apenas números são permitidos',
        numericOnly: v => /^\d*$/.test(v) || 'Apenas números são permitidos',
        alphaNumeric: v => !v || /^[a-zA-Z0-9\s\u00C0-\u017F]+$/.test(v) || 'Apenas letras, números e espaços são permitidos'
      }
    }
  },
  watch: {
    initialData: {
      handler(newData) {
        if (newData && Object.keys(newData).length > 0) {
          // Mapear dados da API para o formato do formulário
          this.formData = {
            codigoSecao: newData.codigo_secao || '',
            descricao: newData.descricao || '',
            ativo: newData.ativo !== undefined ? newData.ativo : true
          };
        }
      },
      immediate: true
    }
  },
  methods: {
    validateForm() {
      return this.$refs.form.validate();
    },
    async handleSave() {
      if (!this.validateForm()) {
        this.showErrorMessage('Por favor, preencha todos os campos obrigatórios');
        return;
      }

      this.isSaving = true;
      
      try {
        // Emitir evento para o componente pai
        await this.$emit('save', this.formData);
      
      } catch (error) {
        this.showErrorMessage('Erro ao salvar a seção. Tente novamente.');
      } finally {
        this.isSaving = false;
      }
    },
    handleCancel() {
      this.$emit('cancel');
    },
    clearForm() {
      if (!this.isEditMode) {
        this.formData = {
          codigoSecao: '',
          descricao: '',
          ativo: true
        };
        this.$refs.form.resetValidation();
      }
    },
    showSuccessMessage(message) {
      this.snackbarColor = 'success';
      this.snackbarMessage = message;
      this.showSnackbar = true;
    },
    showErrorMessage(message) {
      this.snackbarColor = 'error';
      this.snackbarMessage = message;
      this.showSnackbar = true;
    },
    // Método para permitir apenas números na digitação
    onlyNumbers(event) {
      const charCode = event.which ? event.which : event.keyCode;
      
      // Bloquear explicitamente pontos e outros caracteres especiais
      if (charCode === 190 || charCode === 110 || charCode === 188 || charCode === 46) {
        event.preventDefault();
        return false;
      }
      
      // Permitir apenas números (48-57) e teclas especiais de navegação
      if (charCode < 48 || charCode > 57) {
        // Permitir apenas teclas especiais: backspace (8), tab (9), enter (13), setas (37-40)
        if (charCode !== 8 && charCode !== 9 && charCode !== 13 && 
            charCode !== 37 && charCode !== 38 && charCode !== 39 && charCode !== 40) {
          event.preventDefault();
          return false;
        }
      }
      
      return true;
    },
    // Método para interceptar colagem e filtrar apenas números
    onPaste(event) {
      event.preventDefault();
      const pastedText = (event.clipboardData || window.clipboardData).getData('text');
      const numericOnly = pastedText.replace(/[^0-9]/g, '');
      this.formData.codigoSecao = numericOnly;
    },
    // Método para filtrar entrada e manter apenas números
    onNumericInput(fieldName, value) {
      // Remove qualquer caractere que não seja número
      const numericValue = value.replace(/[^0-9]/g, '');
      this.formData[fieldName] = numericValue;
    }
  }
}
</script>

<style scoped>
.form-container {
  padding: 8px;
  max-width: 1400px;
  margin: 0 auto;
}

.form-card {
  border-radius: 8px;
  overflow: hidden;
}

.form-header {
  background-color: #F8FAFC;
  border-bottom: 1px solid #E5E7EB;
  padding: 16px 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.form-title {
  font-size: 1.375rem;
  font-weight: 700;
  color: #1F2937;
}

.ativo-checkbox {
  margin: 0;
}

.form-content {
  padding: 16px 20px;
}

.form-row {
  margin: 0;
}

.form-field {
  margin: 0;
}

.form-field .v-input__control {
  min-height: 44px;
}

.form-field .v-input__slot {
  border-radius: 6px;
  border: 1px solid #D1D5DB;
  background-color: #FFFFFF;
  transition: all 0.2s ease;
}

.form-field .v-input__slot:hover {
  border-color: #16A34A;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.form-field .v-input__slot:focus-within {
  border-color: #16A34A;
  border-width: 2px;
  box-shadow: 0 0 0 3px rgba(22, 163, 74, 0.1);
}

/* Labels padrão (não selecionados) */
.form-field .v-label {
  font-size: 0.875rem;
  color: #6B7280;
  font-weight: 500;
  transform: translateY(0);
  transition: all 0.2s ease;
}

/* Labels quando o campo está focado ou tem valor */
.form-field .v-label--active {
  color: #16A34A;
  font-weight: 600;
  transform: translateY(-12px) scale(0.75);
}

/* Labels flutuantes (quando o campo tem valor ou está focado) */
.form-field .v-label--floating {
  background-color: #FFFFFF;
  padding: 0 4px;
  margin-left: 8px;
  border-radius: 4px;
  transform: translateY(-12px) scale(0.75);
  color: #16A34A;
  font-weight: 600;
}

/* Estado focado para todos os tipos de campo */
.form-field.v-input--is-focused .v-label--floating,
.form-field.v-input--is-focused .v-label--active {
  color: #16A34A;
  font-weight: 600;
}

/* Ajuste específico para campos de texto */
.form-field.v-text-field .v-label--floating {
  transform: translateY(-12px) scale(0.75);
}

.form-field.v-text-field .v-label--active {
  transform: translateY(-12px) scale(0.75);
}

.form-actions {
  background-color: #F8FAFC;
  border-top: 1px solid #E5E7EB;
  padding: 16px 20px;
  display: flex;
  justify-content: center;
  gap: 16px;
}

.action-btn {
  min-width: 120px;
  height: 40px;
  font-weight: 600;
  text-transform: none;
  border-radius: 6px;
}

.cancel-btn {
  border: 2px solid #9CA3AF;
  color: #6B7280;
}

.cancel-btn:hover {
  background-color: #F3F4F6;
  border-color: #6B7280;
}

.save-btn {
  background-color: #16A34A;
  color: white;
}

.save-btn:disabled {
  background-color: #D1D5DB;
  color: #9CA3AF;
}

/* Responsividade */
@media (max-width: 960px) {
  .form-container {
    padding: 6px;
  }
  
  .form-header {
    padding: 12px 16px;
    flex-direction: column;
    gap: 12px;
    align-items: flex-start;
  }
  
  .form-content {
    padding: 12px 16px;
  }
  
  .form-actions {
    padding: 12px 16px;
    flex-direction: column;
    align-items: center;
  }
  
  .action-btn {
    width: 100%;
    max-width: 200px;
  }
}

@media (max-width: 600px) {
  .form-container {
    padding: 4px;
  }
  
  .form-header {
    padding: 10px 12px;
  }
  
  .form-content {
    padding: 10px 12px;
  }
  
  .form-actions {
    padding: 10px 12px;
  }
  
  .form-title {
    font-size: 1.25rem;
  }
}
</style>
